/**
 * Copyright © 2020-2026 Delicious AI, LLC
 */

import { ApolloProvider } from '@apollo/client'
import { CacheProvider } from '@emotion/react'
import { EmotionCache } from '@emotion/cache'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useAuthListener, useLoggedInUser } from '@dai/common'
import { client } from 'api/client'
import { auth } from 'api/Firebase'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useRoutes,
} from 'react-router-dom'

import useT from '@/i18n/useT'
import useTheme from '@/hooks/useTheme'
import createEmotionCache from '@/utils/createEmotionCache'

import LoginContainer from './containers/LoginContainer'
import { UserProvider } from './context/UserContext'
import routes from './routes'
import createTheme from './theme'

dayjs.extend(duration)

export const ApolloClientGate: React.FC = () => {
  return (
    // Keep Apollo infrastructure in place for future GraphQL usage.
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  )
}

const clientSideEmotionCache = createEmotionCache()

interface AppProps {
  emotionCache?: EmotionCache
}

function App({ emotionCache = clientSideEmotionCache }: AppProps) {
  const content = useRoutes(routes)
  const { theme } = useTheme()
  const { t } = useT()

  const matchRoot = useMatch({ path: '/' })
  const onLogin = useMatch({ path: '/login' })
  const SKIP_AUTH = import.meta.env.VITE_APP__SKIP_AUTH === 'true'
  const {
    state: userInfo,
    handle: { handleRefetch },
  } = useLoggedInUser(SKIP_AUTH ? null : 'mlUltra')

  useAuthListener(SKIP_AUTH ? null : auth, handleRefetch)

  // This is needed to scroll to the top of the page on route change
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  if (!SKIP_AUTH && userInfo.loading) {
    return null
  }

  if (!SKIP_AUTH && !onLogin && !userInfo.user && !userInfo.loading) {
    return <Navigate to="/login" />
  }

  if (!SKIP_AUTH && matchRoot && userInfo.user && !userInfo.loading) {
    return <Navigate to="/stats" />
  }

  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet defaultTitle={t('app.defaultTitle', 'Labeling')} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MuiThemeProvider theme={createTheme(theme)}>
            <UserProvider value={userInfo}>
              <Routes>
                <Route path="/login" element={<LoginContainer />} />
                <Route path="/*" element={content} />
              </Routes>
            </UserProvider>
          </MuiThemeProvider>
        </LocalizationProvider>
      </HelmetProvider>
    </CacheProvider>
  )
}

export default App
